import {
  Backdrop,
  Button,
  ButtonProps,
  CircularProgress,
  Divider,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';
import classNames from 'classnames';
import {FeatureConstants, getOrCreateFeatureFlag} from 'Core/ducks/featureFlag';
import {createSnackbar} from 'Core/ducks/snackbar';
import {Async, isLoaded} from 'Core/models/Async';
import {getOrElse} from 'fp-ts/lib/Option';
import {DeletePayload, getDeletePayload} from 'Quoting/api/apiHelpers';
import {clearCompensationDetails, deleteCompensation, getValidatePayloadSave} from 'Quoting/ducks/compensation';
import {
  calculateRates,
  clearQuoteFormData,
  getQuoteDetails,
  setSelectedProduct,
  updateIsCalculatingRates,
  updateIsSavingQuote,
  updateQuote,
} from 'Quoting/ducks/createQuote';
import {List as ImmutableList} from 'immutable';

import {setSelectedOpportunityId} from 'Quoting/ducks/opportunity';
import {clearCompensationDecisionTableResponse} from 'Quoting/ducks/quoteActions';
import {clearRatingReportDetails} from 'Quoting/ducks/ratings';
import {CompensationPayload, CompensationValue} from 'Quoting/models/DTOs/CompensationPayload';
import {QuoteStatus} from 'Quoting/models/enum';
import {A360ProductModel} from 'Quoting/models/Quoting';
import {QuotingRouteConstants} from 'Quoting/Routes';
import {
  getValueFromSessionStorage,
  isA360HealthProduct,
  isDefined,
  isQuoteNotEditable,
  parsePath,
  safeRenderValue,
  VALUE_NOT_AVAILABLE,
} from 'Quoting/utils/utilities';
import React, {useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps, useHistory, useLocation, useParams} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import {AllstateCardV2} from 'Shared/components/AllstateCardV2';
import Loading from 'Shared/components/Loading';
import {LocalStorageKey} from 'Shared/utilities/localStorage';
import {parseQueryString} from 'Shared/utilities/parsers';
import {themeColors} from 'Theme';
import {getProductByModel} from '../Opportunity/ProductSelection';
import {OpportunityTitle} from '../Opportunity/QuotingLandingPage';
import {MultiStepQuoteForm} from './MultiStepQuoteForm';
import {QuoteDetailsHeader} from './QuoteDetailsHeader';
import {RatePremiumTableView} from './RatePremiumTable/RatePremiumTable';
import {getRateAggregatePolicyPremium} from './RatePremiumTable/RatePremiumTableUtils';
import {useQuoteField} from '../hooks/useHandleQuoteFieldChange';

const useQuoteFormTitleStyles = makeStyles(() => ({
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',

    '& > div:nth-child(2)': {
      display: 'flex',
    },
  },
  cardBtnContainer: {
    alignItems: 'center',
    '& > :nth-child(2)': {
      margin: '0 1rem',
    },
  },
  saveQuoteSpinner: {
    position: 'absolute',
    top: '25%',
    left: '40%',
    marginTop: -12,
    marginLeft: -12,
  },
  saveBtnWrapper: {
    position: 'relative',
  },

  cardBtnDisabled: {
    background: '#d9d9d9',
  },

  cardBtn: {
    fontSize: '0.875rem',
    fontWeight: 600,
    textTransform: 'capitalize',
    lineHeight: '1.05rem',
    border: 'none',
    borderRadius: '0.3125rem',
    padding: '0.75rem 1.5rem',
  },

  cancelBtn: {
    color: themeColors.gray_800,
    backgroundColor: themeColors.gray_100,
    '&:hover': {
      backgroundColor: themeColors.gray_70,
      color: themeColors.gray_800,
    },
  },

  saveAndExitBtn: {
    background: themeColors.brightBlue_100,
    color: themeColors.blue_600,
    '&:hover': {
      backgroundColor: themeColors.blue_400,
      color: themeColors.blue_600,
    },
    '&:disabled': {
      color: themeColors.gray_300,
      backgroundColor: themeColors.brightBlue_100,
    },
  },

  saveBtn: {
    background: themeColors.blue_400,
    color: themeColors.primaryWhite,
    '&:hover': {
      backgroundColor: themeColors.brightBlue_300,
      color: themeColors.primaryWhite,
    },
    '&:disabled': {
      color: themeColors.primaryWhite,
      background: themeColors.blue_200,
    },
  },
}));

const checkEmpty = (value: CompensationValue, compensationPayload: Async<CompensationPayload>) => {
  return isLoaded(compensationPayload) && value.type.length === 0 && value.rate.length === 0;
};

const QuoteFormCardTitle = (props: ButtonProps) => {
  const dispatch = useDispatch();

  const {getQuoteFieldValue} = useQuoteField();
  const history = useHistory();
  const {selectedOpportunityId} = useSelector((state: ReducerState) => state.opportunity);
  const {quote, isSavingQuote, isCalculatingRates} = useSelector((state: ReducerState) => state.quoteForm);
  const classes = useQuoteFormTitleStyles({disabled: props.disabled});

  const customerName = getValueFromSessionStorage(LocalStorageKey.CustomerName);

  const {
    compensationPayload,
    deleteFlag,
    initResponse,
    compensationValue,
    compensationError,
    searchValue,
  } = useSelector((state: ReducerState) => state.compensation);

  const isDisabledSaveQuote = !isLoaded(quote) || isSavingQuote || isCalculatingRates || isQuoteNotEditable(quote);

  const handleCancel = () => {
    const opportunityId =
      selectedOpportunityId && selectedOpportunityId !== ''
        ? selectedOpportunityId
        : getValueFromSessionStorage(LocalStorageKey.OpportunityId);

    if (opportunityId) {
      history.push(QuotingRouteConstants.OPPORTUNITY_SUMMARY(opportunityId));
    }
  };

  const deleteCompensationFn = (shouldExit, selectedOpportunityId) => {
    const deletePayload: DeletePayload = isLoaded(initResponse)
      ? getDeletePayload(initResponse.Data)
      : isLoaded(searchValue) && getDeletePayload(searchValue.Data[0]);
    dispatch(deleteCompensation(deletePayload, shouldExit, selectedOpportunityId));
  };

  const handleUpdate = (shouldExit: boolean = false) => {
    const noPlansAdded: boolean =
      !isDefined(getQuoteFieldValue<ImmutableList<any>>('benefitPackagingDetail.plans')) ||
      (isDefined(getQuoteFieldValue<ImmutableList<any>>('benefitPackagingDetail.plans')) &&
        getQuoteFieldValue<ImmutableList<any>>('benefitPackagingDetail.plans')?.size === 0);
    if (quote) {
      dispatch(updateIsSavingQuote(true));
      if (compensationError) {
        dispatch(createSnackbar(compensationError));
        return;
      }
      if (((!deleteFlag && isLoaded(compensationPayload)) || compensationValue.rate.length !== 0) && !noPlansAdded) {
        //@ts-ignore
        dispatch(getValidatePayloadSave(compensationPayload.Data, shouldExit, selectedOpportunityId, true));
      } else if (
        (deleteFlag &&
          (isLoaded(initResponse) || isLoaded(searchValue)) &&
          checkEmpty(compensationValue, compensationPayload)) ||
        (noPlansAdded && (isLoaded(initResponse) || (isLoaded(searchValue) && searchValue.Data[0] != undefined)))
      ) {
        deleteCompensationFn(shouldExit, selectedOpportunityId);
      } else {
        dispatch(updateQuote(shouldExit, selectedOpportunityId));
      }
    }
  };

  return (
    <div className={classes.titleContainer}>
      <OpportunityTitle
        opportunityId={selectedOpportunityId ?? ''}
        policyNumber={isLoaded(quote) ? safeRenderValue(quote?.Data?.get('policyNumber')) : VALUE_NOT_AVAILABLE}
        customerName={customerName}
      />
      <div className={classes.cardBtnContainer}>
        <Button className={classNames(classes.cardBtn, classes.cancelBtn)} onClick={handleCancel}>
          Cancel
        </Button>
        <div className={classes.saveBtnWrapper}>
          <Button
            className={classNames(classes.cardBtn, classes.saveAndExitBtn)}
            disabled={isDisabledSaveQuote}
            onClick={() => handleUpdate(true)}
          >
            Save & Exit
          </Button>
          {isSavingQuote && <CircularProgress size={24} className={classes.saveQuoteSpinner} />}
        </div>
        <div className={classes.saveBtnWrapper}>
          <Button
            className={classNames(classes.cardBtn, classes.saveBtn)}
            disabled={isDisabledSaveQuote}
            onClick={() => handleUpdate()}
          >
            Save
          </Button>
          {isSavingQuote && <CircularProgress size={24} className={classes.saveQuoteSpinner} />}
        </div>
      </div>
    </div>
  );
};

const useCalculateRatesStyles = makeStyles(() => ({
  premiumContainer: {
    padding: '1rem 2rem',
  },
  quotePremiumContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  quotePremiumLabel: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: '1.35rem',
    textTransform: 'uppercase',
    marginRight: '0.5rem',
  },
  quotePremiumValue: {
    color: '#3ba00a',
    // color: '#2f8100',
    fontSize: '1rem',
    fontWeight: 600,
  },
  calculateRateBtn: {
    '& span': {
      fontSize: '0.875rem',
    },
    color: '#FFF',
    textTransform: 'capitalize',
    borderRadius: '0.3125rem',
    marginRight: '0.5rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: '1.05rem',
    border: 'none',
    padding: '0.75rem 1.5rem',
    background: '#004EA8',
    '&:hover': {
      backgroundColor: '#2FA0FF',
      color: '#FFFFFF',
    },
  },
  ratePremiumTable: {
    padding: '0.5rem 2rem',
    flexWrap: 'nowrap',
  },
  expandMoreLink: {
    fontSize: '1rem',
    textTransform: 'capitalize',
    fontWeight: 500,
    lineHeight: '1.05rem',
    color: '#004ea8',
    margin: '1rem 2rem',
    '& span': {
      fontSize: '1rem',
    },
  },
  calclateRateBtnDisabled: {
    backgroundColor: '#E8E8E8',
    color: '#474747',
  },
  calculateRatesSpinner: {
    position: 'absolute',
    top: '25%',
    left: '40%',
    marginTop: -12,
    marginLeft: -12,
  },
  calculateRatesBtnWrapper: {
    margin: '1rem',
    position: 'relative',
  },
}));

const CalculateRates = (props: ButtonProps) => {
  const {
    customerCensusClasses,
    isCalculatingRates,
    isSavingQuote,
    selectedProduct,
    quote,
    quoteRatesAndPremium,
  } = useSelector((state: ReducerState) => state.quoteForm);

  const {selectedOpportunityId} = useSelector((state: ReducerState) => state.opportunity);

  const {compensationValue, deleteFlag, compensationPayload, initResponse, searchValue} = useSelector(
    (state: ReducerState) => state.compensation
  );

  const classes = useCalculateRatesStyles({...props});
  const dispatch = useDispatch();

  const quoteStatus = isLoaded(quote) ? quote.Data.get('state') : '';

  const isDisabledCalculateRates =
    (quoteStatus !== QuoteStatus.DataGather && isDefined(compensationValue.rate)) ||
    isCalculatingRates ||
    isSavingQuote;

  const quotePremium = useMemo(
    () =>
      quoteStatus === QuoteStatus.DataGather
        ? 'N/A'
        : getRateAggregatePolicyPremium(quoteRatesAndPremium?.rateAggregates),
    [quoteRatesAndPremium?.rateAggregates, quote]
  );

  const handleCalculateRates = async () => {
    if (isLoaded(quote)) {
      dispatch(updateIsCalculatingRates(true));
      if ((!deleteFlag && isLoaded(compensationPayload)) || compensationValue.rate.length !== 0) {
        await new Promise<void>(resolve => {
          //@ts-ignore
          dispatch(getValidatePayloadSave(compensationPayload.Data, false, selectedOpportunityId, false, resolve));
        });
      } else if (
        deleteFlag &&
        (isLoaded(initResponse) || isLoaded(searchValue)) &&
        checkEmpty(compensationValue, compensationPayload)
      ) {
        const deletePayload: DeletePayload = isLoaded(initResponse)
          ? getDeletePayload(initResponse.Data)
          : //@ts-ignore
            getDeletePayload(searchValue.Data[0]);
        dispatch(deleteCompensation(deletePayload, false, selectedOpportunityId));
      }
      dispatch(calculateRates(quote.Data, customerCensusClasses));
      return;
    }
    dispatch(createSnackbar('Invalid quote data!'));
  };

  return (
    <div>
      <Grid container direction="column">
        <Grid container justifyContent="space-between" className={classes.premiumContainer}>
          <Grid item className={classes.quotePremiumContainer}>
            {isA360HealthProduct(selectedProduct?._model) ? (
              <>
                <Typography className={classes.quotePremiumLabel}>Annualized Premium: </Typography>
                {isDefined(quotePremium) && (
                  <Typography className={classes.quotePremiumValue}>{` ${quotePremium}`}</Typography>
                )}
              </>
            ) : (
              <div></div>
            )}
          </Grid>
          <Grid>
            <div className={classes.calculateRatesBtnWrapper}>
              <Button
                className={classNames(classes.calculateRateBtn, {
                  [classes.calclateRateBtnDisabled]: isDisabledCalculateRates,
                })}
                disabled={isDisabledCalculateRates}
                onClick={handleCalculateRates}
              >
                Calculate Rates
              </Button>
              {isCalculatingRates && <CircularProgress size={24} className={classes.calculateRatesSpinner} />}
            </div>
          </Grid>
        </Grid>
        <Divider style={{marginTop: '1rem'}} />
        <Grid container direction="column" className={classes.ratePremiumTable}>
          <RatePremiumTableView />
        </Grid>
      </Grid>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  separator: {
    border: '1.5px solid #ececef',
  },
  createQuoteTitle: {
    fontSize: '1.125rem',
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
}));

type CreateQuoteFormProps = RouteComponentProps<{quoteId?: string}>;

export const CreateQuoteForm = (_props: CreateQuoteFormProps) => {
  const styles = useStyles();
  const {quote, isLoadingCreateQuotePage, isBackDropOpen} = useSelector((state: ReducerState) => state.quoteForm);
  const dispatch = useDispatch();
  const {search} = useLocation();
  const getQueryStringValue = parseQueryString(search);
  const opportunityIdValue = getQueryStringValue('opportunityId');

  const {modelName, quoteId, revisionNo} = useParams<{
    modelName: string;
    quoteId: string;
    revisionNo: string;
  }>();

  useEffect(() => {
    return () => {
      dispatch(clearQuoteFormData());
      dispatch(clearCompensationDetails());
      dispatch(clearRatingReportDetails());
      dispatch(clearCompensationDecisionTableResponse());
    };
  }, []);

  useEffect(() => {
    dispatch(getOrCreateFeatureFlag(FeatureConstants.BR_QUOTING_TOOL_EIS_VALIDATION));
    if (
      !isLoaded(quote) &&
      isDefined(modelName) &&
      isDefined(quoteId) &&
      isDefined(revisionNo) &&
      !Number.isNaN(Number(revisionNo))
    ) {
      const opportunityId =
        getOrElse(() => '')(opportunityIdValue) === ''
          ? getValueFromSessionStorage(LocalStorageKey.OpportunityId)
          : getOrElse(() => '')(opportunityIdValue);

      dispatch(setSelectedProduct(getProductByModel(modelName as A360ProductModel)));
      dispatch(setSelectedOpportunityId(opportunityId));
      dispatch(getQuoteDetails(modelName as A360ProductModel, quoteId, Number(revisionNo), opportunityId, false));
    }
  }, [quote.key]);

  return (
    <div style={{padding: '2rem'}}>
      {isLoadingCreateQuotePage ? (
        <Loading />
      ) : (
        <React.Fragment>
          <header aria-label="create quote">
            <Typography component="h1" className={styles.createQuoteTitle}>
              Create Quote
            </Typography>
            <hr className={styles.separator} />
          </header>
          <main>
            <div>
              <Backdrop className={styles.backdrop} open={isBackDropOpen}>
                <CircularProgress color="inherit" />
              </Backdrop>
            </div>
            <AllstateCardV2 title={<QuoteFormCardTitle />}>
              <QuoteDetailsHeader />
              <hr className={styles.separator} />
              <CalculateRates />
              <MultiStepQuoteForm
                productModel={
                  isLoaded(quote) ? (quote.Data.get('_modelName') as A360ProductModel) : ('' as A360ProductModel)
                }
                planCd={
                  isLoaded(quote)
                    ? (quote.Data?.getIn(parsePath('benefitPackagingDetail.plans[0].planCd')) as string)
                    : ''
                }
              />
            </AllstateCardV2>
          </main>
        </React.Fragment>
      )}
    </div>
  );
};
